import type React from "react"

interface AttendanceRecord {
  id: string
  student_id: string
  student_name?: string | null
  class_name?: string | null
  class_date?: string | null
  class_time?: string | null
  status?: string | null
  instructor_notes?: string | null
}

interface ClassGroup {
  className: string
  classDate: string
  classTime: string
  students: AttendanceRecord[]
}

interface PrintableAttendanceSheetProps {
  records: AttendanceRecord[]
  filters?: {
    className?: string
    dateFrom?: string
    dateTo?: string
    sessionName?: string
  }
}

function groupByClassAndDate(records: AttendanceRecord[]): ClassGroup[] {
  const groups = new Map<string, ClassGroup>()

  for (const record of records) {
    const className = record.class_name || "Unknown Class"
    const classDate = record.class_date || "Unknown Date"
    const classTime = record.class_time || ""
    const key = `${className}|${classDate}`

    if (!groups.has(key)) {
      groups.set(key, { className, classDate, classTime, students: [] })
    }
    groups.get(key)!.students.push(record)
  }

  // Sort students within each group by name
  for (const group of groups.values()) {
    group.students.sort((a, b) =>
      (a.student_name || "").localeCompare(b.student_name || ""),
    )
  }

  // Sort groups by date then class name
  return Array.from(groups.values()).sort((a, b) => {
    const dateCompare = a.classDate.localeCompare(b.classDate)
    if (dateCompare !== 0) return dateCompare
    return a.className.localeCompare(b.className)
  })
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return dateStr
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  } catch {
    return dateStr
  }
}

export const PrintableAttendanceSheet: React.FC<
  PrintableAttendanceSheetProps
> = ({ records, filters }) => {
  const groups = groupByClassAndDate(records)

  if (groups.length === 0) return null

  return (
    <div className="printable-attendance-sheet">
      <style>{`
        .printable-attendance-sheet {
          display: none;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          .printable-attendance-sheet,
          .printable-attendance-sheet * {
            visibility: visible;
          }
          .printable-attendance-sheet {
            display: block !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            color: #000;
            background: #fff;
          }
          .attendance-sheet-page {
            page-break-after: always;
            padding: 20px 30px;
          }
          .attendance-sheet-page:last-child {
            page-break-after: auto;
          }
          .sheet-header {
            text-align: center;
            margin-bottom: 16px;
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
          }
          .sheet-header h1 {
            font-size: 18px;
            font-weight: bold;
            margin: 0 0 4px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .sheet-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 4px 24px;
            margin-bottom: 16px;
            font-size: 12px;
          }
          .sheet-meta-item {
            display: flex;
            gap: 4px;
          }
          .sheet-meta-label {
            font-weight: bold;
          }
          .roster-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .roster-table th,
          .roster-table td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
          }
          .roster-table th {
            background: #e8e8e8;
            font-weight: bold;
            font-size: 11px;
            text-transform: uppercase;
          }
          .roster-table td.checkbox-col {
            width: 30px;
            text-align: center;
            font-size: 16px;
          }
          .roster-table td.num-col {
            width: 30px;
            text-align: center;
          }
          .roster-table td.notes-col {
            width: 35%;
          }
          .roster-table td.name-col {
            width: 25%;
          }
          .sheet-footer {
            margin-top: 24px;
            padding-top: 16px;
          }
          .sheet-total {
            font-weight: bold;
            margin-bottom: 24px;
          }
          .signature-line {
            display: flex;
            gap: 40px;
            margin-top: 16px;
          }
          .signature-field {
            flex: 1;
          }
          .signature-field .line {
            border-bottom: 1px solid #000;
            height: 24px;
            margin-bottom: 4px;
          }
          .signature-field .label {
            font-size: 10px;
            color: #555;
          }
        }
      `}</style>
      {groups.map((group, groupIdx) => (
        <div className="attendance-sheet-page" key={`${group.className}-${group.classDate}-${groupIdx}`}>
          <div className="sheet-header">
            <h1>Attendance Sheet</h1>
            <div style={{ fontSize: "14px", fontWeight: "bold" }}>
              {group.className}
            </div>
          </div>

          <div className="sheet-meta">
            <div className="sheet-meta-item">
              <span className="sheet-meta-label">Date:</span>
              <span>{formatDate(group.classDate)}</span>
            </div>
            {group.classTime && (
              <div className="sheet-meta-item">
                <span className="sheet-meta-label">Time:</span>
                <span>{group.classTime}</span>
              </div>
            )}
            {filters?.sessionName && (
              <div className="sheet-meta-item">
                <span className="sheet-meta-label">Season:</span>
                <span>{filters.sessionName}</span>
              </div>
            )}
          </div>

          <table className="roster-table">
            <thead>
              <tr>
                <th style={{ width: "30px" }}>#</th>
                <th>Student Name</th>
                <th style={{ width: "30px" }}>P</th>
                <th style={{ width: "30px" }}>A</th>
                <th style={{ width: "30px" }}>L</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {group.students.map((student, idx) => (
                <tr key={student.id}>
                  <td className="num-col">{idx + 1}</td>
                  <td className="name-col">
                    {student.student_name || student.student_id}
                  </td>
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="notes-col" />
                </tr>
              ))}
              {/* Extra blank rows for walk-ins */}
              {[...Array(3)].map((_, idx) => (
                <tr key={`blank-${idx}`}>
                  <td className="num-col">{group.students.length + idx + 1}</td>
                  <td className="name-col" />
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="checkbox-col">{"\u25A1"}</td>
                  <td className="notes-col" />
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sheet-footer">
            <div className="sheet-total">
              Total Students: {group.students.length}
            </div>
            <div className="signature-line">
              <div className="signature-field">
                <div className="line" />
                <div className="label">Instructor Signature</div>
              </div>
              <div className="signature-field">
                <div className="line" />
                <div className="label">Date</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
